import React, { PropTypes } from 'react';
import { AuthorizedComponent } from 'react-router-role-authorization';
import { connect } from 'react-redux';
import { browserHistory, withRouter } from 'react-router';
import moment from "moment";
import DateTime from 'react-datetime';
import _ from 'lodash';

import SortedDropboxesSelector from "../selectors/SortedDropboxesSelector";
import {
    ASSIGNMENTS__REQUESTED,
    DROPBOX__USERS__REQUESTED,
    DROPBOXES__REQUESTED,
    DROPBOXES__ROLES__REQUESTED,
    LOGS__REQUESTED,
    LOGS__CLEAR__REQUESTED,
    ROLES__SELF__REQUESTED
} from "../constants/actionTypes";
import { INSTRUCTOR } from "../constants/roles";

class LogPage extends AuthorizedComponent {
    constructor(props) {
        super(props);

        this.userRoles = props.auth.roles;
        this.notAuthorizedPath = '/not-found';

        this.state = {
            assignment_id: undefined,
            assignments: [],
            date_from: null,
            date_to: null,
            event: "",
            input_props_date_from: null,
            input_props_date_to: null,
            ip_address: "",
            dropbox_id: undefined,
            dropboxes: {},
            filter_assignment: "",
            filter_username: "",
            filtered_logs: [],
            logs: {},
            method: "",
            precept: undefined,
            precepts: [],
            referer: "",
            uri: "",
            user_id: undefined,
            usernames: [],
            val: "",
            // Multi-value filter arrays (for modal selections)
            selected_ip_addresses: [],
            selected_methods: [],
            selected_uris: [],
            selected_referers: [],
            selected_events: [],
            selected_values: [],
            selected_user_ids: [],
            offset: 0,
            hasMore: true,
            loadingMore: false,
            filtersCollapsed: false,
            sortField: 'date',
            sortDirection: 'desc',
            selectedLog: null,
            showModal: false
        };

        this.lastCollapseChange = 0;
        this.lastActionWasCollapse = false;

        this.handleFromDateChange = this.handleFromDateChange.bind(this);
        this.handleToDateChange = this.handleToDateChange.bind(this);
        this.handleDropboxFilterChange = this.handleDropboxFilterChange.bind(this);
        this.handleUserFilterChange = this.handleUserFilterChange.bind(this);
        this.handlePreceptFilterChange = this.handlePreceptFilterChange.bind(this);
        this.handleAssignmentFilterChange = this.handleAssignmentFilterChange.bind(this);
        this.handleIpAddressFilterChange = this.handleIpAddressFilterChange.bind(this);
        this.handleURIFilterChange = this.handleURIFilterChange.bind(this);
        this.handleRefererFilterChange = this.handleRefererFilterChange.bind(this);
        this.handleValueFilterChange = this.handleValueFilterChange.bind(this);
        this.handleEventFilterChange = this.handleEventFilterChange.bind(this);
        this.handleRoleFilterChange = this.handleRoleFilterChange.bind(this);
        this.handleMethodFilterChange = this.handleMethodFilterChange.bind(this);
        this.loadMoreLogs = this.loadMoreLogs.bind(this);
        this.handleScrollRaw = this.handleScrollRaw.bind(this);
        this.toggleFilters = this.toggleFilters.bind(this);
        this.handleSort = this.handleSort.bind(this);
        this.handleLogClick = this.handleLogClick.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.toggleFilterFromModal = this.toggleFilterFromModal.bind(this);
        this.removeFilterValue = this.removeFilterValue.bind(this);
        this.refreshLogsWithFilters = this.refreshLogsWithFilters.bind(this);
        this.handleScroll = _.debounce(this.handleScrollRaw, 100);
        this.onChangeIPAddressDebounced = _.debounce(this.onChangeIPAddressDebounced, 1000);
        this.onChangeURIDebounced = _.debounce(this.onChangeURIDebounced, 1000);
        this.onChangeRefererDebounced = _.debounce(this.onChangeRefererDebounced, 1000);
        this.onChangeValueDebounced = _.debounce(this.onChangeValueDebounced, 1000);
        this.onChangeEventDebounced = _.debounce(this.onChangeEventDebounced, 1000);
        this.onChangeMethodDebounced = _.debounce(this.onChangeMethodDebounced, 1000);
    }

    componentDidMount() {
        const { dispatch } = this.props;

        dispatch({
            type: ROLES__SELF__REQUESTED
        });

        // Add scroll listener to window for infinite scroll
        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        // Clean up scroll listener
        window.removeEventListener('scroll', this.handleScroll);
    }

    componentDidUpdate(prevProps) {
        const { assignments, dispatch, dropboxes, logs, memberships, roles_self, submissions, users } = this.props;

        if (prevProps.roles_self !== roles_self) {
            if (this.hasInstructorRole()) {
                if (_.size(dropboxes) < 1) {
                    dispatch({
                        type: DROPBOXES__REQUESTED,
                    });
                }
                if (_.size(assignments) < 1) {
                    dispatch({
                        type: ASSIGNMENTS__REQUESTED,
                    });
                }
            } else {
                browserHistory.push('/not-found');
            }
        }

        // Check if new logs were loaded and update hasMore flag
        if (prevProps.logs !== logs && this.state.loadingMore) {
            // Check how many logs the API actually returned (not how many unique ones were added)
            const returnedCount = logs.__meta ? logs.__meta.returnedCount : 0;

            // If the API returned fewer than 100 logs, we've reached the end
            const hasMore = returnedCount >= 100;

            // Update state once with all changes
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                loadingMore: false,
                hasMore: hasMore
            }, () => {
                // After loading more, check if we need to load even more to fill the page
                this.checkAndLoadMoreIfNeeded();
            });
        }
    }

    checkAndLoadMoreIfNeeded() {
        // Check if page has enough content to scroll
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        // If page content doesn't fill the viewport (with some buffer) and we have more logs, load more
        // Add 200px buffer to ensure we load more even if there's just a little bit of scrollable space
        if (scrollHeight <= clientHeight + 200 && this.state.hasMore && !this.state.loadingMore && this.state.dropbox_id) {
            this.loadMoreLogs();
        }
    }

    handleFromDateChange(dateFrom) {
        this.setState({
            date_from: dateFrom,
            input_props_date_from : moment(dateFrom).format('YYYY-MM-DD HH:mm:ss')
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                dateFrom,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handleToDateChange(dateTo) {
        this.setState({
            date_to: dateTo,
            input_props_date_to : moment(dateTo).format('YYYY-MM-DD HH:mm:ss')
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                dateTo,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handleUserFilterChange(event) {
        const userId = parseInt(event.target.value);

        this.setState({
            ...this.state,
            user_id : userId,
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                userId,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handleAssignmentFilterChange(event) {
        const assignmentId = parseInt(event.target.value);

        this.setState({
            ...this.state,
            assignment_id: assignmentId,
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                assignmentId,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handleDropboxFilterChange(event) {
        const dropboxId = parseInt(event.target.value);

        this.setState({
            ...this.state,
            dropbox_id: dropboxId,
            assignment_id: "0",
            precept: "0",
            user_id: "0"
        });

        if (event.target.value >= 0) {
            this.props.dispatch({
                type: DROPBOX__USERS__REQUESTED,
                payload: {
                    dropboxId: dropboxId
                }
            });

            this.props.dispatch({
                type: DROPBOXES__ROLES__REQUESTED,
                payload: {
                    dropboxId: dropboxId,
                },
            });
        }

        if (!this.hasFormErrors()) {
            this.getLogs(
                "0",  // Use new reset value instead of this.state.assignment_id
                dropboxId,
                "0",  // Use new reset value instead of this.state.precept
                "0",  // Use new reset value instead of this.state.user_id
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handlePreceptFilterChange(event) {
        const precept = event.target.value;

        this.setState({
            ...this.state,
            precept : precept,
            user_id: "0",
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    handleIpAddressFilterChange(event) {
        const ipAddress = event.target.value;

        this.setState({
            ...this.state,
            ip_address : ipAddress,
        });

        this.onChangeIPAddressDebounced(ipAddress);
    }

    handleURIFilterChange(event) {
        const uri = event.target.value;

        this.setState({
            ...this.state,
            uri : uri,
        });

        this.onChangeURIDebounced(uri);
    }

    handleRefererFilterChange(event) {
        const referer = event.target.value;

        this.setState({
            ...this.state,
            referer : referer,
        });

        this.onChangeRefererDebounced(referer);
    }

    handleValueFilterChange(event) {
        const val = event.target.value;

        this.setState({
            ...this.state,
            val : val,
        });

        this.onChangeValueDebounced(val);
    }

    handleMethodFilterChange(event) {
        const method = event.target.value;

        this.setState({
            ...this.state,
            method : method,
        });

        this.onChangeMethodDebounced(method);
    }

    handleEventFilterChange(event) {
        this.setState({
            ...this.state,
            event : event.target.value,
        });

        this.onChangeEventDebounced(event.target.value);
    }

    handleRoleFilterChange(event) {
        const role = this.state.dropbox_id ? event.target.value : null;

        this.setState({
            ...this.state,
            role : role,
        });

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                role,
            );
        }
    }

    onChangeIPAddressDebounced(ipAddress) {
        if (this.state.dropbox_id && !this.hasFormErrors()) {
            if (this.state.dropbox_id && this.isValidIpAddress(ipAddress)) {
                this.getLogs(
                    this.state.assignment_id,
                    this.state.dropbox_id,
                    this.state.precept,
                    this.state.user_id,
                    this.state.date_to,
                    this.state.date_from,
                    ipAddress,
                    this.state.uri,
                    this.state.referer,
                    this.state.val,
                    this.state.method,
                    this.state.event,
                    this.state.role,
                );
            } else {
                if (this.state.dropbox_id && ipAddress === "") {
                    this.getLogs(
                        this.state.assignment_id,
                        this.state.dropbox_id,
                        this.state.precept,
                        this.state.user_id,
                        this.state.date_to,
                        this.state.date_from,
                        null,
                        this.state.uri,
                        this.state.referer,
                        this.state.val,
                        this.state.method,
                        this.state.event,
                        this.state.role,
                    );
                }
            }
        }
    }

    onChangeURIDebounced(uri) {
        uri = this.state.dropbox_id ? uri : null;

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    onChangeValueDebounced(val) {
        val = this.state.dropbox_id ? val : null;

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    onChangeMethodDebounced(method) {
        method = this.state.dropbox_id ? method : null;

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                method,
                this.state.event,
                this.state.role,
            );
        }
    }

    onChangeEventDebounced(event) {
        event = this.state.dropbox_id ? event : null;

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                this.state.referer,
                this.state.val,
                this.state.method,
                event,
                this.state.role,
            );
        }
    }

    onChangeRefererDebounced(referer) {
        referer = this.state.dropbox_id ? referer : null;

        if (this.state.dropbox_id && !this.hasFormErrors()) {
            this.getLogs(
                this.state.assignment_id,
                this.state.dropbox_id,
                this.state.precept,
                this.state.user_id,
                this.state.date_to,
                this.state.date_from,
                this.state.ip_address,
                this.state.uri,
                referer,
                this.state.val,
                this.state.method,
                this.state.event,
                this.state.role,
            );
        }
    }

    isValidIpAddress(ipAddress) {
        return ipAddress.match(/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/);
    }

    hasFormErrors() {
        if (this.state.ip_address !== "" && !this.isValidIpAddress(this.state.ip_address)) {
            return true;
        }

        return false;
    }

    hasInstructorRole() {
        const { roles_self } = this.props;
        return _.includes(
            _.flatMap(
                roles_self,
                dropbox_role => {
                    return dropbox_role.role;
                }),
            INSTRUCTOR
        );
    }

    mapSortFieldToBackend(field) {
        // Map frontend field names (snake_case) to backend field names (camelCase)
        const fieldMap = {
            'date': 'date',
            'username': 'username',
            'ip_address': 'ipAddress',
            'method': 'method',
            'uri': 'uri',
            'referer': 'referer',
            'event': 'event',
            'value': 'value',
            'user_agent': 'userAgent'
        };
        return fieldMap[field] || 'date';
    }

    getLogs(assignmentId, dropboxId, precept, userId, to, from, ipAddress, uri, referer, val, method, event, role) {
        // Clear existing logs and reset offset when filters change
        this.props.dispatch({
            type: LOGS__CLEAR__REQUESTED
        });

        this.setState({
            offset: 0,
            hasMore: true,
            loadingMore: false
        });

        this.props.dispatch({
            type: LOGS__REQUESTED,
            payload: {
                assignmentId: assignmentId > 0 ? assignmentId : null,
                dropboxId: dropboxId,
                precept: precept === "0" ? null : precept,
                userId: userId > 0 ? userId : null,
                to: to ? moment(to).unix() : null,
                from: from ? moment(from).unix() : null,
                ipAddress: ipAddress,
                uri: uri,
                referer: referer,
                val: val,
                method: method === "0" ? null : method,
                event: event === "0" ? null : event,
                role: role,
                offset: 0,
                limit: 100,
                append: false,
                sortField: this.mapSortFieldToBackend(this.state.sortField),
                sort: this.state.sortDirection
            }
        });
    }

    loadMoreLogs() {
        if (this.state.loadingMore || !this.state.hasMore || !this.state.dropbox_id || this.props.request.sendingRequest) {
            return;
        }

        this.setState({ loadingMore: true });

        const newOffset = this.state.offset + 100;

        // Combine array filters with single value filters (same as refreshLogsWithFilters)
        const ipFilters = [...this.state.selected_ip_addresses];
        if (this.state.ip_address && this.isValidIpAddress(this.state.ip_address)) {
            ipFilters.push(this.state.ip_address);
        }

        const methodFilters = [...this.state.selected_methods];
        if (this.state.method && this.state.method !== "0") {
            methodFilters.push(this.state.method);
        }

        const uriFilters = [...this.state.selected_uris];
        if (this.state.uri) {
            uriFilters.push(this.state.uri);
        }

        const refererFilters = [...this.state.selected_referers];
        if (this.state.referer) {
            refererFilters.push(this.state.referer);
        }

        const eventFilters = [...this.state.selected_events];
        if (this.state.event && this.state.event !== "0") {
            eventFilters.push(this.state.event);
        }

        const valueFilters = [...this.state.selected_values];
        if (this.state.val) {
            valueFilters.push(this.state.val);
        }

        const userIdFilters = [...this.state.selected_user_ids];
        if (this.state.user_id && this.state.user_id > 0) {
            userIdFilters.push(this.state.user_id);
        }

        this.props.dispatch({
            type: LOGS__REQUESTED,
            payload: {
                assignmentId: this.state.assignment_id > 0 ? this.state.assignment_id : null,
                dropboxId: this.state.dropbox_id,
                precept: this.state.precept === "0" ? null : this.state.precept,
                userId: userIdFilters.length > 0 ? _.uniq(userIdFilters).join(',') : null,
                to: this.state.date_to ? moment(this.state.date_to).unix() : null,
                from: this.state.date_from ? moment(this.state.date_from).unix() : null,
                ipAddress: ipFilters.length > 0 ? _.uniq(ipFilters).join(',') : null,
                uri: uriFilters.length > 0 ? _.uniq(uriFilters).join(',') : null,
                referer: refererFilters.length > 0 ? _.uniq(refererFilters).join(',') : null,
                val: valueFilters.length > 0 ? _.uniq(valueFilters).join(',') : null,
                method: methodFilters.length > 0 ? _.uniq(methodFilters).join(',') : null,
                event: eventFilters.length > 0 ? _.uniq(eventFilters).join(',') : null,
                role: this.state.role,
                offset: newOffset,
                limit: 100,
                append: true,
                sortField: this.mapSortFieldToBackend(this.state.sortField),
                sort: this.state.sortDirection
            }
        });

        this.setState({ offset: newOffset });
    }

    handleScrollRaw() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;

        const scrolledToBottom = scrollHeight - scrollTop <= clientHeight + 100;

        const now = Date.now();
        const timeSinceLastChange = now - this.lastCollapseChange;
        const shouldCollapse = scrollTop > 300;

        // Only apply guard when trying to do the OPPOSITE action
        // This prevents layout-shift feedback loops while allowing intentional scrolling
        if (this.state.filtersCollapsed !== shouldCollapse) {
            const tryingToExpand = !shouldCollapse && this.state.filtersCollapsed;
            const tryingToCollapse = shouldCollapse && !this.state.filtersCollapsed;

            // If last action was collapse, guard against immediate expansion
            // If last action was expand, guard against immediate collapse
            const shouldApplyGuard =
                (this.lastActionWasCollapse && tryingToExpand) ||
                (!this.lastActionWasCollapse && tryingToCollapse);

            if (!shouldApplyGuard || timeSinceLastChange > 500) {
                this.setState({ filtersCollapsed: shouldCollapse });
                this.lastCollapseChange = now;
                this.lastActionWasCollapse = shouldCollapse;
            }
        }

        // Infinite scroll
        if (scrolledToBottom && this.state.hasMore && !this.state.loadingMore) {
            this.loadMoreLogs();
        }
    }

    toggleFilters() {
        // First, open the filters
        this.setState({ filtersCollapsed: false }, () => {
            // Then scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
        // Reset the timer and mark as expansion action
        this.lastCollapseChange = Date.now();
        this.lastActionWasCollapse = false;
    }

    handleSort(field) {
        // If clicking the same field, toggle direction. Otherwise, default to ascending
        const newDirection = this.state.sortField === field && this.state.sortDirection === 'asc' ? 'desc' : 'asc';

        this.setState({
            sortField: field,
            sortDirection: newDirection
        }, () => {
            // Reload logs with new sort order if we have a selected course
            if (this.state.dropbox_id) {
                this.getLogs(
                    this.state.assignment_id,
                    this.state.dropbox_id,
                    this.state.precept,
                    this.state.user_id,
                    this.state.date_to,
                    this.state.date_from,
                    this.state.ip_address,
                    this.state.uri,
                    this.state.referer,
                    this.state.val,
                    this.state.method,
                    this.state.event,
                    this.state.role
                );
            }
        });
    }

    handleLogClick(log) {
        this.setState({
            selectedLog: log,
            showModal: true
        });
    }

    closeModal() {
        this.setState({
            selectedLog: null,
            showModal: false
        });
    }

    toggleFilterFromModal(field, value, isChecked) {
        // Toggle the checkbox - add or remove from array
        switch(field) {
            case 'username':
                if (value && this.state.dropbox_id) {
                    const user = _.find(this.props.users, (u) => u.username === value);
                    if (user) {
                        const newIds = isChecked
                            ? [...this.state.selected_user_ids.filter(id => id !== user.id)]
                            : [...this.state.selected_user_ids, user.id];
                        // If there's exactly one user selected, update the dropdown to show it
                        // Otherwise, reset to "0" (blank)
                        const newUserId = newIds.length === 1 ? newIds[0] : "0";
                        this.setState({
                            selected_user_ids: newIds,
                            user_id: newUserId
                        }, () => {
                            this.refreshLogsWithFilters();
                        });
                    }
                }
                break;
            case 'ip_address':
                if (value && this.isValidIpAddress(value)) {
                    this.setState({
                        selected_ip_addresses: [],
                        ip_address: isChecked ? "" : value
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            case 'method':
                if (value) {
                    this.setState({
                        selected_methods: [],
                        method: isChecked ? "" : value
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            case 'uri':
                if (value) {
                    // Decode the URI to ensure it's in raw form before storing
                    const decodedUri = isChecked ? "" : decodeURIComponent(value);
                    this.setState({
                        selected_uris: [],
                        uri: decodedUri
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            case 'referer':
                if (value) {
                    this.setState({
                        selected_referers: [],
                        referer: isChecked ? "" : value
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            case 'event':
                if (value) {
                    this.setState({
                        selected_events: [],
                        event: isChecked ? "" : value
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            case 'value':
                if (value) {
                    this.setState({
                        selected_values: [],
                        val: isChecked ? "" : value
                    }, () => {
                        this.refreshLogsWithFilters();
                    });
                }
                break;
            default:
                break;
        }
    }

    removeFilterValue(field, value) {
        switch(field) {
            case 'username':
                this.setState({
                    selected_user_ids: this.state.selected_user_ids.filter(id => id !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'ip_address':
                this.setState({
                    selected_ip_addresses: this.state.selected_ip_addresses.filter(ip => ip !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'method':
                this.setState({
                    selected_methods: this.state.selected_methods.filter(m => m !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'uri':
                this.setState({
                    selected_uris: this.state.selected_uris.filter(u => u !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'referer':
                this.setState({
                    selected_referers: this.state.selected_referers.filter(r => r !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'event':
                this.setState({
                    selected_events: this.state.selected_events.filter(e => e !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            case 'value':
                this.setState({
                    selected_values: this.state.selected_values.filter(v => v !== value)
                }, () => {
                    this.refreshLogsWithFilters();
                });
                break;
            default:
                break;
        }
    }

    refreshLogsWithFilters() {
        if (!this.state.dropbox_id) return;

        // Combine array filters with single value filters
        const ipFilters = [...this.state.selected_ip_addresses];
        if (this.state.ip_address && this.isValidIpAddress(this.state.ip_address)) {
            ipFilters.push(this.state.ip_address);
        }

        const methodFilters = [...this.state.selected_methods];
        if (this.state.method && this.state.method !== "0") {
            methodFilters.push(this.state.method);
        }

        const uriFilters = [...this.state.selected_uris];
        if (this.state.uri) {
            uriFilters.push(this.state.uri);
        }

        const refererFilters = [...this.state.selected_referers];
        if (this.state.referer) {
            refererFilters.push(this.state.referer);
        }

        const eventFilters = [...this.state.selected_events];
        if (this.state.event && this.state.event !== "0") {
            eventFilters.push(this.state.event);
        }

        const valueFilters = [...this.state.selected_values];
        if (this.state.val) {
            valueFilters.push(this.state.val);
        }

        const userIdFilters = [...this.state.selected_user_ids];
        if (this.state.user_id && this.state.user_id > 0) {
            userIdFilters.push(this.state.user_id);
        }

        const uriParam = uriFilters.length > 0 ? _.uniq(uriFilters).join(',') : undefined;

        // Build filter params - send arrays as comma-separated strings (deduplicated)
        this.getLogs(
            this.state.assignment_id,
            this.state.dropbox_id,
            this.state.precept,
            userIdFilters.length > 0 ? _.uniq(userIdFilters).join(',') : undefined,
            this.state.date_to,
            this.state.date_from,
            ipFilters.length > 0 ? _.uniq(ipFilters).join(',') : undefined,
            uriParam,
            refererFilters.length > 0 ? _.uniq(refererFilters).join(',') : undefined,
            valueFilters.length > 0 ? _.uniq(valueFilters).join(',') : undefined,
            methodFilters.length > 0 ? _.uniq(methodFilters).join(',') : undefined,
            eventFilters.length > 0 ? _.uniq(eventFilters).join(',') : undefined,
            this.state.role
        );
    }

    disableLogFields(){
        return this.props.request.sendingRequest || !this.state.dropbox_id;
    }


    render() {
        // Filter out the __meta property and sort by current sort field/direction
        const getSortValue = (log) => {
            // Handle nested properties like user.username
            if (this.state.sortField === 'username') {
                return log.user ? log.user.username : '';
            }
            return log[this.state.sortField];
        };

        const sortedLogs = _.orderBy(
            _.values(_.omit(this.props.logs, '__meta')),
            [getSortValue],
            [this.state.sortDirection]
        );

        const logsContainerStyle = {
            // No fixed height or overflow - let the page scroll naturally
        };

        const tableStyle = {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px'
        };

        const thStyle = {
            position: 'sticky',
            top: 0,
            backgroundColor: '#f5f5f5',
            padding: '10px 8px',
            textAlign: 'left',
            borderBottom: '2px solid #ddd',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            zIndex: 1,
            cursor: 'pointer',
            userSelect: 'none'
        };

        const getSortIndicator = (field) => {
            if (this.state.sortField === field) {
                return this.state.sortDirection === 'asc' ? ' ↑' : ' ↓';
            }
            return '';
        };

        const tdStyle = {
            padding: '8px',
            borderBottom: '1px solid #eee',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        };

        const trStyle = {
            cursor: 'pointer'
        };

        const trHoverStyle = {
            ...trStyle,
            backgroundColor: '#f9f9f9'
        };

        const loadingStyle = {
            textAlign: 'center',
            padding: '20px',
            color: '#666'
        };

        const filterToggleStyle = {
            position: 'fixed',
            top: '60px',
            right: '15px',
            zIndex: 1000
        };

        const toggleButtonStyle = {
            padding: '8px 16px',
            backgroundColor: '#337ab7',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        };

        return (
            <div className="col-md-12 logs-filter">
                {this.state.filtersCollapsed && (
                    <div style={filterToggleStyle}>
                        <button style={toggleButtonStyle} onClick={this.toggleFilters}>
                            Show Filters
                        </button>
                    </div>
                )}
                <div style={{ display: this.state.filtersCollapsed ? 'none' : 'block' }}>
                    <h3 style={{ marginTop: '0', marginBottom: '15px' }}>Filters</h3>
                    <div style={{
                        backgroundColor: '#f9f9f9',
                        padding: '20px',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                        marginBottom: '20px'
                    }}>
                    <div className="row">
                        <div className="control-group col-md-12">
                            <div>
                                <label>Course</label>
                                <br/>
                                <select value={this.state.dropbox_id} onChange={this.handleDropboxFilterChange} className="form-control" disabled={this.props.request.sendingRequest}>
                                    <option value="0"/>
                                    {
                                        _.map(
                                            SortedDropboxesSelector(this.props), (dropbox) => {
                                                return (
                                                    <option key={`dropbox_${dropbox.id}`} value={dropbox.id}>
                                                        {dropbox.name}
                                                    </option>
                                                );
                                            }
                                        )
                                    }
                                </select>
                            </div>
                        </div>
                    </div>
                    <hr/>
                    <div className="row">
                    <div className="control-group col-md-4">
                        <div>
                            <label>Assignment</label>
                            <br/>
                            <select value={this.state.assignment_id} onChange={this.handleAssignmentFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option value="0"/>
                                {
                                    _.map(
                                        this.props.assignments, (assignment) => {
                                            return this.state.dropbox_id !== undefined && this.state.dropbox_id === assignment.dropbox.id ? (
                                                <option key={`dropbox_${this.state.dropbox_id}_assignment_${assignment.id}`} value={assignment.id}>
                                                    {assignment.name}
                                                </option>
                                            ): null;
                                        }
                                    )
                                }
                            </select>
                        </div>
                        <div>
                            <label>Precept</label>
                            <br/>
                            <select value={this.state.precept} onChange={this.handlePreceptFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option value="0"/>
                                {
                                    _.map(
                                        _.sortBy(
                                            _.uniqWith(
                                                _.filter(
                                                    this.props.roles,
                                                    (role) => {
                                                        return role && role.precept;
                                                    }
                                                ),
                                                (a,b) => {
                                                    return a.precept === b.precept;
                                                }
                                            ),
                                            'precept'
                                        ),
                                        (role, i) => {
                                            return (
                                                <option key={`dropbox_${this.state.dropbox_id}_precept_${i}`} value={role.precept}>{role.precept}</option>
                                            );
                                        }
                                    )
                                }
                            </select>
                        </div>
                        <div>
                            <label>Role</label>
                            <br/>
                            <select value={this.state.role} onChange={this.handleRoleFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option/>
                                <option>Student</option>
                                <option>Instructor</option>
                            </select>
                        </div>
                        <div>
                            <label>Username</label>
                            <br/>
                            <select value={this.state.user_id} onChange={this.handleUserFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option value="0"/>
                                {
                                    this.state.precept === undefined || this.state.precept === "0" ?
                                        _.map(
                                            _.sortBy(
                                                this.props.users,
                                                'username'
                                            ), (user) => {
                                                return (
                                                    <option key={`dropbox_${this.state.dropbox_id}_user_${user.id}`} value={user.id}>
                                                        {user.username}
                                                    </option>
                                                );
                                            }
                                        )
                                    :
                                        _.map(
                                            _.sortBy(
                                                this.props.roles,
                                                (role) => {
                                                    return role.user.username;
                                                }
                                            ), (role) => {
                                                return role.precept === this.state.precept ?
                                                    (
                                                        <option key={`dropbox_${this.state.dropbox_id}_user_${role.user.id}`} value={role.user.id}>
                                                            {role.user.username}
                                                        </option>
                                                    )
                                                :
                                                    null
                                                ;
                                            }
                                        )
                                }
                            </select>
                        </div>
                    </div>
                    <div className="control-group col-md-4">
                        <div>
                            <label>From</label>
                            <br/>
                            <DateTime dateFormat="YYYY-MM-DD" closeOnSelect={true} onChange={this.handleFromDateChange} timeFormat="HH:mm:ss" inputProps={{name: "input_props_date_from", disabled:this.disableLogFields()}} required />
                        </div>
                        <div>
                            <label>To</label>
                            <br/>
                            <DateTime dateFormat="YYYY-MM-DD" closeOnSelect={true} onChange={this.handleToDateChange} timeFormat="HH:mm:ss" inputProps={{name: "input_props_date_to", disabled:this.disableLogFields()}} required />
                        </div>
                        <div>
                            <label>Method</label>
                            <br/>
                            <select value={this.state.method} onChange={this.handleMethodFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option value="0"/>
                                <option value="DELETE">DELETE</option>
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                            </select>
                        </div>
                        <div>
                            <label>Event</label>
                            <br/>
                            <select value={this.state.event} onChange={this.handleEventFilterChange} className="form-control" disabled={this.disableLogFields()}>
                                <option value="0"/>
                                <option value="access">access</option>
                                <option value="copy">copy</option>
                                <option value="create">create</option>
                                <option value="delete">delete</option>
                                <option value="error">error</option>
                                <option value="update">update</option>
                                <option value="upload">upload</option>
                            </select>
                        </div>
                    </div>
                    <div className="control-group col-md-4">
                        <div>
                            <label>IP Address</label>
                            <br/>
                            <input
                                value={this.state.ip_address}
                                onChange={this.handleIpAddressFilterChange}
                                className={
                                    this.isValidIpAddress(this.state.ip_address) || this.state.ip_address === "" ?
                                        'form-control'
                                    :
                                        'form-control input-error'
                                }
                                disabled={this.disableLogFields()}
                            />
                        </div>
                        <div>
                            <label>URI</label>
                            <br/>
                            <input
                                value={this.state.uri}
                                onChange={this.handleURIFilterChange}
                                className="form-control"
                                disabled={this.disableLogFields()}
                            />
                        </div>
                        <div>
                            <label>Referer</label>
                            <br/>
                            <input
                                value={this.state.referer}
                                onChange={this.handleRefererFilterChange}
                                className="form-control"
                                disabled={this.disableLogFields()}
                            />
                        </div>
                        <div>
                            <label>Value</label>
                            <br/>
                            <input
                                value={this.state.val}
                                onChange={this.handleValueFilterChange}
                                className="form-control"
                                disabled={this.disableLogFields()}
                            />
                        </div>
                    </div>
                    </div>
                </div>
                </div>
                <div style={logsContainerStyle}>
                    {sortedLogs.length === 0 && this.state.dropbox_id ? (
                        <div style={loadingStyle}>
                            {this.props.request.sendingRequest ? 'Loading logs...' : 'No logs to display'}
                        </div>
                    ) : (
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle} onClick={() => this.handleSort('date')}>
                                        Date{getSortIndicator('date')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('username')}>
                                        Username{getSortIndicator('username')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('ip_address')}>
                                        IP Address{getSortIndicator('ip_address')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('method')}>
                                        Method{getSortIndicator('method')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('uri')}>
                                        URI{getSortIndicator('uri')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('referer')}>
                                        Referer{getSortIndicator('referer')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('event')}>
                                        Event{getSortIndicator('event')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('value')}>
                                        Value{getSortIndicator('value')}
                                    </th>
                                    <th style={thStyle} onClick={() => this.handleSort('user_agent')}>
                                        User Agent{getSortIndicator('user_agent')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {_.map(sortedLogs, (log) => (
                                    <tr key={log.id} style={trStyle} onClick={() => this.handleLogClick(log)}>
                                        <td style={tdStyle} title={log.date}>{log.date}</td>
                                        <td style={tdStyle} title={log.user ? log.user.username : ''}>{log.user ? log.user.username : ''}</td>
                                        <td style={tdStyle} title={log.ip_address}>{log.ip_address}</td>
                                        <td style={tdStyle} title={log.method}>{log.method}</td>
                                        <td style={tdStyle} title={log.uri}>{log.uri}</td>
                                        <td style={tdStyle} title={log.referer}>{log.referer}</td>
                                        <td style={tdStyle} title={log.event}>{log.event}</td>
                                        <td style={tdStyle} title={log.value}>{log.value}</td>
                                        <td style={tdStyle} title={log.user_agent}>{log.user_agent}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {this.state.loadingMore && (
                        <div style={loadingStyle}>Loading more logs...</div>
                    )}
                    {!this.state.hasMore && sortedLogs.length > 0 && (
                        <div style={loadingStyle}>No more logs to display</div>
                    )}
                </div>
                {this.state.showModal && this.state.selectedLog && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }} onClick={this.closeModal}>
                        <div style={{
                            backgroundColor: 'white',
                            padding: '30px',
                            borderRadius: '8px',
                            maxWidth: '800px',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ margin: 0 }}>Log Details</h3>
                                <button onClick={this.closeModal} style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    color: '#999'
                                }}>×</button>
                            </div>
                            <div style={{ fontSize: '14px' }}>
                                <div style={{
                                    backgroundColor: '#f9f9f9',
                                    padding: '15px',
                                    borderRadius: '4px',
                                    marginBottom: '20px',
                                    border: '1px solid #e0e0e0'
                                }}>
                                    <div style={{
                                        marginBottom: '12px',
                                        fontWeight: 'bold',
                                        color: '#337ab7',
                                        fontSize: '13px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Click checkboxes to filter
                                    </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {(() => {
                                        const username = this.state.selectedLog.user ? this.state.selectedLog.user.username : null;
                                        const user = username ? _.find(this.props.users, (u) => u.username === username) : null;
                                        const isSelected = user && this.state.selected_user_ids.includes(user.id);
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('username', username, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <strong>Username:</strong>
                                                <span style={{ marginLeft: '5px' }}>{username || 'N/A'}</span>
                                            </label>
                                        );
                                    })()}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {(() => {
                                        const isSelected = this.state.selected_ip_addresses.includes(this.state.selectedLog.ip_address) ||
                                                          this.state.ip_address === this.state.selectedLog.ip_address;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('ip_address', this.state.selectedLog.ip_address, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <strong>IP Address:</strong>
                                                <span style={{ marginLeft: '5px' }}>{this.state.selectedLog.ip_address}</span>
                                            </label>
                                        );
                                    })()}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {(() => {
                                        const isSelected = this.state.selected_methods.includes(this.state.selectedLog.method) ||
                                                          this.state.method === this.state.selectedLog.method;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('method', this.state.selectedLog.method, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <strong>Method:</strong>
                                                <span style={{ marginLeft: '5px' }}>{this.state.selectedLog.method}</span>
                                            </label>
                                        );
                                    })()}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {(() => {
                                        // Decode both URIs for comparison to handle encoding differences
                                        const decodedLogUri = decodeURIComponent(this.state.selectedLog.uri);
                                        const decodedStateUri = this.state.uri ? decodeURIComponent(this.state.uri) : "";
                                        const isSelected = this.state.selected_uris.includes(this.state.selectedLog.uri) ||
                                                          decodedStateUri === decodedLogUri;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('uri', this.state.selectedLog.uri, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px', marginTop: '3px', flexShrink: 0 }}
                                                />
                                                <div>
                                                    <strong>URI:</strong>
                                                    <span style={{ marginLeft: '5px', wordBreak: 'break-all' }}>{this.state.selectedLog.uri}</span>
                                                </div>
                                            </label>
                                        );
                                    })()}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {this.state.selectedLog.referer ? (() => {
                                        const isSelected = this.state.selected_referers.includes(this.state.selectedLog.referer) ||
                                                          this.state.referer === this.state.selectedLog.referer;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('referer', this.state.selectedLog.referer, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px', marginTop: '3px', flexShrink: 0 }}
                                                />
                                                <div>
                                                    <strong>Referer:</strong>
                                                    <span style={{ marginLeft: '5px', wordBreak: 'break-all' }}>{this.state.selectedLog.referer}</span>
                                                </div>
                                            </label>
                                        );
                                    })() : <div><strong>Referer:</strong> N/A</div>}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {(() => {
                                        const isSelected = this.state.selected_events.includes(this.state.selectedLog.event) ||
                                                          this.state.event === this.state.selectedLog.event;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('event', this.state.selectedLog.event, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <strong>Event:</strong>
                                                <span style={{ marginLeft: '5px' }}>{this.state.selectedLog.event}</span>
                                            </label>
                                        );
                                    })()}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    {this.state.selectedLog.value ? (() => {
                                        const isSelected = this.state.selected_values.includes(this.state.selectedLog.value) ||
                                                          this.state.val === this.state.selectedLog.value;
                                        return (
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleFilterFromModal('value', this.state.selectedLog.value, isSelected);
                                                    }}
                                                    style={{ marginRight: '8px', marginTop: '3px', flexShrink: 0 }}
                                                />
                                                <div>
                                                    <strong>Value:</strong>
                                                    <span style={{ marginLeft: '5px', wordBreak: 'break-all' }}>{this.state.selectedLog.value}</span>
                                                </div>
                                            </label>
                                        );
                                    })() : <div><strong>Value:</strong> N/A</div>}
                                </div>
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    <strong>Date:</strong> {this.state.selectedLog.date}
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    <strong>User Agent:</strong> <div style={{ wordBreak: 'break-all' }}>{this.state.selectedLog.user_agent}</div>
                                </div>
                            </div>
                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button onClick={this.closeModal} style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#337ab7',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}>Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

LogPage.propTypes = {
    dispatch: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
};

function mapStateToProps(state) {

    const { assignments, auth, dropboxes, logs, memberships, request, roles, roles_self, submissions, users } = state;

    return { assignments, auth, dropboxes, logs, memberships, request, roles, roles_self, submissions, users };
}

export default connect(mapStateToProps)(withRouter(LogPage));



/** WEBPACK FOOTER **
 ** ./src/containers/LogPage.js
 **/